<template>
    <div class="gif-generator-container">
        <div class="tool-left">
            <div class="gif-generator-card">
                <h1 class="title">🎥 视频转GIF</h1>
                <p class="subtitle">从视频中提取精彩片段，生成流畅GIF动图</p>

                <!-- 拖拽上传区域 -->
                <div
                        class="upload-area"
                        @dragover.prevent
                        @dragenter.prevent
                        @drop="handleDrop"
                        :class="{ 'drag-over': isDragging }"
                >
                    <div class="upload-content">
                        <svg class="upload-icon" viewBox="0 0 24 24" width="48" height="48">
                            <path
                                    fill="currentColor"
                                    d="M18,10.7V8.5C18,7.1 16.9,6 15.5,6H5.5C4.1,6 3,7.1 3,8.5V15.5C3,16.9 4.1,18 5.5,18H10V16H5.5C5.2,16 5,15.8 5,15.5V8.5C5,8.2 5.2,8 5.5,8H15.5C15.8,8 16,8.2 16,8.5V13H18V10.7M19,10V14L23,12L19,10Z"
                            />
                        </svg>
                        <p class="upload-text">拖拽视频到此处或点击上传</p>
                        <p class="upload-hint">支持 MP4, WebM, MOV 等常见格式</p>
                        <input
                                type="file"
                                ref="fileInput"
                                accept="video/*"
                                @change="handleVideoUpload"
                                class="file-input"
                        />
                        <button class="upload-button" @click="$refs.fileInput.click()">
                            选择视频
                        </button>
                    </div>
                </div>

                <!-- 视频预览与剪辑 -->
                <div v-if="videoUrl" class="section">
                    <h2 class="section-title">✂️ 剪辑视频片段</h2>
                    <div class="video-preview-wrapper">
                        <video
                                ref="videoPlayer"
                                :src="videoUrl"
                                @loadedmetadata="onVideoLoaded"
                                class="video-preview"
                                :style="{ cursor: isSelecting ? 'col-resize' : 'default' }"
                                @mousedown="startSelect"
                                @mousemove="updateSelect"
                                @mouseup="endSelect"
                                @mouseleave="endSelect"
                        ></video>

                        <!-- 时间选择条 -->
                        <div class="time-selector">
                            <div
                                    class="selection-range"
                                    :style="selectionStyle"
                                    v-if="selectionStart !== null"
                            ></div>
                        </div>

                        <!-- 时间显示 -->
                        <div class="time-display">
                            <span>开始: {{ formatTime(selectionStartSec) }}</span>
                            <span>结束: {{ formatTime(selectionEndSec) }}</span>
                            <span>时长: {{ formatTime(durationSec) }}</span>
                        </div>
                    </div>
                </div>

                <!-- 生成设置 -->
                <div v-if="videoUrl" class="section settings">
                    <h2 class="section-title">⚙️ 生成设置</h2>
                    <div class="settings-grid">
                        <div class="setting-item">
                            <label for="fps">帧率 (FPS):</label>
                            <select id="fps" v-model.number="fps">
                                <option :value="5">5 FPS</option>
                                <option :value="10">10 FPS</option>
                                <option :value="15">15 FPS</option>
                                <option :value="24">24 FPS</option>
                                <option :value="30">30 FPS</option>
                            </select>
                        </div>
                        <div class="setting-item">
                            <label for="repeat">循环次数:</label>
                            <select id="repeat" v-model.number="repeatCount">
                                <option :value="0">无限循环</option>
                                <option :value="1">播放一次</option>
                                <option :value="2">播放两次</option>
                                <option :value="3">播放三次</option>
                            </select>
                        </div>
                        <div class="setting-item">
                            <label for="quality">质量:</label>
                            <select id="quality" v-model.number="quality">
                                <option :value="1">高质量 (慢)</option>
                                <option :value="10">标准质量</option>
                                <option :value="30">低质量 (快)</option>
                            </select>
                        </div>
                    </div>
                </div>

                <!-- 生成按钮 -->
                <div class="action-area">
                    <button
                            @click="generateGif"
                            :disabled="isGenerating || !videoUrl"
                            class="generate-btn"
                            :class="{ 'generating': isGenerating }"
                    >
                        <span v-if="!isGenerating">✨ 生成 GIF</span>
                        <span v-else>⏳ 生成中... {{ progress }}%</span>
                    </button>

                    <button v-if="videoUrl" @click="clearAll" class="clear-btn">
                        🗑️ 清空视频
                    </button>
                </div>

                <!-- GIF 预览 -->
                <div v-if="gifUrl" class="section gif-result">
                    <h2 class="section-title">🎉 生成成功!</h2>
                    <div class="gif-preview-container">
                        <img :src="gifUrl" alt="生成的GIF" class="gif-preview" />
                        <div class="gif-info">
                            <p><strong>尺寸:</strong> {{ gifDimensions.width }} × {{ gifDimensions.height }}</p>
                            <p><strong>大小:</strong> {{ gifSize }}</p>
                            <p><strong>帧率:</strong> {{ fps }} FPS</p>
                        </div>
                        <a :href="gifUrl" download="video_to_gif.gif" class="download-btn">
                            ⬇️ 下载 GIF
                        </a>
                    </div>
                </div>
            </div>
        </div>

        <!-- 内容说明区 -->
        <div class="tool-content">
            <h2>🎥 视频转 GIF 在线工具</h2>
            <p>从 MP4、WebM、MOV 视频中提取片段，制作高清 GIF 动图。支持可视化时间轴剪辑、帧率调节、循环设置，制作表情包、精彩片段、教程演示。</p>

            <h3>✨ 功能特点</h3>
            <ul>
                <li><strong>可视化剪辑</strong>：时间轴拖拽选择片段，精确到帧</li>
                <li><strong>帧率可调</strong>：5-30 FPS，平衡流畅度与文件大小</li>
                <li><strong>尺寸控制</strong>：可设置输出 GIF 的宽度</li>
                <li><strong>格式广泛</strong>：支持 MP4、WebM、MOV 等常见视频格式</li>
            </ul>

            <h3>📝 使用教程</h3>
            <ol>
                <li>上传视频文件或粘贴在线视频链接</li>
                <li>在时间轴上拖动选择要转换的片段</li>
                <li>设置帧率、循环次数、输出尺寸</li>
                <li>点击「生成 GIF」并下载</li>
            </ol>

            <h3>💡 参数建议</h3>
            <p><strong>表情包</strong>：10 FPS，宽度 480px，循环无限<br>
            <strong>精彩片段</strong>：15 FPS，宽度 640px，循环 3 次<br>
            <strong>教程演示</strong>：8 FPS，宽度 800px，循环无限</p>

            <h3>🔒 隐私说明</h3>
            <p>使用 Canvas 逐帧提取在浏览器本地处理，视频不会上传到服务器。</p>
        </div>
    </div>
</template>

<script>
    import seoMixin from '../js/seo-mixin';

export default {
        name: 'VideoToGif',
        mixins: [seoMixin],
        data() {
            return {
                seoTitle: '视频转GIF在线工具 - FreeToolBox',
                seoDescription: '免费在线视频转GIF工具，从MP4、WebM、MOV视频中截取片段制作GIF动图。支持帧率、循环次数、质量调节，可视化时间轴剪辑。',
                seoKeywords: '视频转gif,视频制作动图,mp4转gif,视频截取gif,在线gif制作',
                videoUrl: null,
                gifUrl: null,
                isGenerating: false,
                progress: 0,
                fps: 10,
                repeatCount: 0,
                quality: 10,
                gifDimensions: { width: 0, height: 0 },
                gifSize: '0 KB',

                // 视频元信息
                videoDuration: 0,
                selectionStart: null,
                selectionEnd: null,
                isSelecting: false,

                // 拖拽状态
                isDragging: false,
            };
        },
        computed: {
            selectionStyle() {
                if (this.selectionStart === null || this.selectionEnd === null) return {};
                const start = (this.selectionStart / this.videoDuration) * 100;
                const width = ((this.selectionEnd - this.selectionStart) / this.videoDuration) * 100;
                return {
                    left: `${start}%`,
                    width: `${width}%`,
                };
            },
            selectionStartSec() {
                return this.selectionStart !== null ? this.selectionStart.toFixed(2) : '0.00';
            },
            selectionEndSec() {
                return this.selectionEnd !== null ? this.selectionEnd.toFixed(2) : '0.00';
            },
            durationSec() {
                return this.videoDuration.toFixed(2);
            },
        },
        methods: {
            handleVideoUpload(event) {
                const file = event.target.files[0];
                if (!file) return;
                this.loadVideo(file);
            },
            handleDrop(event) {
                event.preventDefault();
                this.isDragging = false;
                const file = event.dataTransfer.files[0];
                if (!file.type.startsWith('video/')) return;
                this.loadVideo(file);
            },
            loadVideo(file) {
                if (this.videoUrl) {
                    URL.revokeObjectURL(this.videoUrl);
                }
                this.videoUrl = URL.createObjectURL(file);
                this.clearGif();
            },
            onVideoLoaded() {
                const video = this.$refs.videoPlayer;
                this.videoDuration = video.duration;
                // 默认选择前3秒或整个视频
                this.selectionStart = 0;
                this.selectionEnd = Math.min(3, video.duration);
            },
            startSelect(e) {
                const rect = this.$refs.videoPlayer.getBoundingClientRect();
                const percent = (e.clientX - rect.left) / rect.width;
                const time = percent * this.videoDuration;
                this.isSelecting = true;
                this.selectionStart = Math.max(0, Math.min(time, this.videoDuration));
                this.selectionEnd = this.selectionStart;
            },
            updateSelect(e) {
                if (!this.isSelecting) return;
                const rect = this.$refs.videoPlayer.getBoundingClientRect();
                const percent = (e.clientX - rect.left) / rect.width;
                const time = percent * this.videoDuration;
                this.selectionEnd = Math.max(0, Math.min(time, this.videoDuration));
                // 保证开始时间小于结束时间
                if (this.selectionEnd < this.selectionStart) {
                    [this.selectionStart, this.selectionEnd] = [this.selectionEnd, this.selectionStart];
                }
            },
            endSelect() {
                this.isSelecting = false;
            },
            formatTime(seconds) {
                return new Date(seconds * 1000).toISOString().substr(11, 8);
            },
            clearAll() {
                if (this.videoUrl) {
                    URL.revokeObjectURL(this.videoUrl);
                    this.videoUrl = null;
                }
                this.clearGif();
                this.selectionStart = null;
                this.selectionEnd = null;
            },
            clearGif() {
                if (this.gifUrl) {
                    URL.revokeObjectURL(this.gifUrl);
                    this.gifUrl = null;
                }
                this.gifDimensions = { width: 0, height: 0 };
                this.gifSize = '0 KB';
            },
            async generateGif() {
                if (!this.videoUrl || this.selectionStart === null || this.selectionEnd === null) return;

                this.isGenerating = true;
                this.progress = 0;
                this.clearGif();

                // 确保 gif.js 已加载
                if (typeof window.GIF === 'undefined') {
                    alert('GIF 库未加载，请检查是否引入了 gif.js 和 gif.worker.js');
                    this.isGenerating = false;
                    return;
                }

                const video = this.$refs.videoPlayer;
                const startTime = this.selectionStart;
                const endTime = this.selectionEnd;
                const duration = endTime - startTime;
                const frameCount = Math.ceil(duration * this.fps);
                const step = 1 / this.fps;

                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                const gif = new window.GIF({
                    workers: 4,
                    quality: this.quality,
                    workerScript: '/gif.worker.js',
                    repeat: this.repeatCount,
                    width: video.videoWidth,
                    height: video.videoHeight,
                });

                canvas.width = video.videoWidth;
                canvas.height = video.videoHeight;

                // 预加载视频到开始时间
                await new Promise((resolve) => {
                    video.currentTime = startTime;
                    video.onseeked = resolve;
                });

                let currentFrame = 0;
                for (let t = startTime; t < endTime; t += step) {
                    video.currentTime = t;
                    await new Promise((resolve) => {
                        video.ontimeupdate = () => {
                            ctx.drawImage(canvas, 0, 0, canvas.width, canvas.height);
                            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                            gif.addFrame(canvas, { delay: step * 1000, copy: true });
                            currentFrame++;
                            this.progress = Math.round((currentFrame / frameCount) * 100);
                            resolve();
                        };
                    });
                }

                gif.on('finished', (blob) => {
                    this.gifUrl = URL.createObjectURL(blob);
                    this.gifDimensions = { width: canvas.width, height: canvas.height };
                    this.gifSize = this.formatFileSize(blob.size);
                    this.isGenerating = false;
                    this.progress = 100;
                });

                gif.render();
            },
            formatFileSize(bytes) {
                if (bytes === 0) return '0 Bytes';
                const k = 1024;
                const sizes = ['Bytes', 'KB', 'MB', 'GB'];
                const i = Math.floor(Math.log(bytes) / Math.log(k));
                return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
            },
        },
        beforeDestroy() {
            if (this.videoUrl) URL.revokeObjectURL(this.videoUrl);
            if (this.gifUrl) URL.revokeObjectURL(this.gifUrl);
        },
    };
</script>

<style scoped>
    .gif-generator-container {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        min-height: 100vh;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        padding: 20px;
        gap: 40px;
    }

    .tool-left {
        flex: 1;
        display: flex;
        justify-content: center;
        max-width: 55%;
    }

    .gif-generator-card {
        background: white;
        border-radius: 16px;
        box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
        padding: 30px;
        max-width: 800px;
        width: 100%;
        backdrop-filter: blur(10px);
    }

    .title {
        text-align: center;
        color: #333;
        margin-bottom: 10px;
        font-size: 2rem;
    }

    .subtitle {
        text-align: center;
        color: #666;
        margin-bottom: 30px;
        font-size: 1.1rem;
    }

    .upload-area {
        border: 2px dashed #ddd;
        border-radius: 12px;
        padding: 40px 20px;
        text-align: center;
        background-color: #f9f9f9;
        transition: all 0.3s ease;
        margin-bottom: 20px;
    }

    .upload-area.drag-over {
        border-color: #409eff;
        background-color: #ecf5ff;
    }

    .upload-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 10px;
    }

    .upload-icon {
        color: #409eff;
    }

    .upload-text {
        font-size: 1.1rem;
        color: #333;
        font-weight: 500;
    }

    .upload-hint {
        color: #999;
        font-size: 0.9rem;
    }

    .upload-button {
        margin-top: 10px;
        padding: 10px 20px;
        background: #409eff;
        color: white;
        border: none;
        border-radius: 6px;
        cursor: pointer;
        font-size: 1rem;
    }

    .upload-button:hover {
        background: #337ecc;
    }

    .file-input {
        display: none;
    }

    .section {
        margin: 30px 0;
    }

    .section-title {
        color: #333;
        margin-bottom: 15px;
        font-size: 1.2rem;
    }

    .video-preview-wrapper {
        position: relative;
        border-radius: 8px;
        overflow: hidden;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }

    .video-preview {
        width: 100%;
        height: auto;
        display: block;
        border-radius: 8px;
    }

    .time-selector {
        position: relative;
        height: 10px;
        background: #eee;
        cursor: col-resize;
    }

    .selection-range {
        position: absolute;
        top: 0;
        height: 100%;
        background: rgba(64, 158, 255, 0.5);
        border: 1px solid #409eff;
        border-radius: 3px;
    }

    .time-display {
        display: flex;
        justify-content: space-between;
        margin-top: 10px;
        font-size: 0.9rem;
        color: #666;
    }

    .settings-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
        gap: 15px;
    }

    .setting-item {
        display: flex;
        flex-direction: column;
    }

    .setting-item label {
        margin-bottom: 5px;
        color: #333;
        font-weight: 500;
    }

    .setting-item select,
    .setting-item input {
        padding: 8px;
        border: 1px solid #ddd;
        border-radius: 4px;
        font-size: 1rem;
    }

    .action-area {
        display: flex;
        gap: 15px;
        flex-wrap: wrap;
        justify-content: center;
        margin: 30px 0;
    }

    .generate-btn,
    .clear-btn {
        padding: 12px 24px;
        border: none;
        border-radius: 8px;
        cursor: pointer;
        font-size: 1rem;
        font-weight: 500;
        transition: all 0.2s ease;
    }

    .generate-btn {
        background: #67c23a;
        color: white;
    }

    .generate-btn:hover:not(:disabled) {
        background: #5daf34;
    }

    .generate-btn.generating {
        background: #909399;
        cursor: not-allowed;
    }

    .clear-btn {
        background: #909399;
        color: white;
    }

    .clear-btn:hover {
        background: #797d82;
    }

    .gif-preview-container {
        text-align: center;
        padding: 20px;
        background: #f8f9fa;
        border-radius: 8px;
        margin-top: 15px;
    }

    .gif-preview {
        max-width: 100%;
        max-height: 400px;
        border-radius: 8px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }

    .gif-info {
        margin: 15px 0;
        color: #555;
        font-size: 0.95rem;
        text-align: left;
    }

    .download-btn {
        display: inline-block;
        padding: 10px 20px;
        background: #e6a23c;
        color: white;
        text-decoration: none;
        border-radius: 6px;
        font-weight: 500;
    }

    .download-btn:hover {
        background: #cf9236;
    }
    /* 内容说明区样式 */
    .tool-content {
        flex: 1;
        max-width: 45%;
        margin: 0;
        padding: 30px;
        background: #f8f9fa;
        border-radius: 12px;
        text-align: left;
    }
    .tool-content h2 {
        color: #333;
        font-size: 1.5em;
        margin-bottom: 15px;
        border-bottom: 2px solid #e91e63;
        padding-bottom: 10px;
    }
    .tool-content h3 {
        color: #555;
        font-size: 1.2em;
        margin: 20px 0 10px;
    }
    .tool-content p, .tool-content li {
        color: #666;
        line-height: 1.8;
        margin-bottom: 10px;
    }
    .tool-content ul, .tool-content ol {
        padding-left: 20px;
        margin-bottom: 15px;
    }
</style>